import './i18n/autoTranslate';
import React, { useEffect, useMemo, useRef, useState , useCallback} from "react";
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Pause,
  Play,
  RotateCcw,
  Square,
  X,
  Sparkles,
  AudioLines,
  Captions,
  ShieldCheck,
  Timer,
  TrendingUp,
  Award,
  CheckCircle2,
  AlertTriangle,
  Download,
  RefreshCcw,
  BarChart3,
  ListChecks,
  ChevronRight,
  Zap,
  Waves,
  Activity,
} from "lucide-react";
import AppBackground from "../components/ui/AppBackground";

// Services (logic only — UI untouched)
import { loadConfiguration } from "../services/interview/configService";
import { generateQuestions, analyzeTextWithBackend, analyzeAnswersWithAI ,submitInterviewResultsPrepared} from "../services/interview/backendService";
import { initializeSpeechRecognition, setupRecognitionCallbacks } from "../services/interview/speechRecognitionService";
import { speakText, stopSpeaking } from "../services/interview/voiceService";

import Confetti from 'react-confetti'
import SEO from "./SEO";

/* -------------------- Mock Data (fallback) -------------------- */
const MOCK_QUESTIONS = [
  "Tell me about yourself and your background.",
  "What interests you most about this position?",
  "Describe a challenging project you worked on and how you handled it.",
  "How do you handle pressure and tight deadlines?",
  "Where do you see yourself professionally in 5 years?",
];

const getQuestionText = (value) => {
  if (value && typeof value === "object") return String(value.question || value.text || "").trim();
  return String(value || "").trim();
};


const STORAGE_CONFIG_KEYS = [
  "finalInterviewConfiguration",
  "interviewConfiguration",
  "currentInterviewConfiguration",
  "phoneInterviewConfiguration",
  "interview_config",
];

const AUTH_TOKEN_KEYS = [
  "access_token",
  "accessToken",
  "token",
  "authToken",
  "jwt",
  "userToken",
];

const safeJsonParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const firstCleanString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const extractVoiceId = (...sources) => {
  const candidates = [];

  const addFromConfig = (obj) => {
    if (!obj || typeof obj !== "object") return;

    candidates.push(
      obj.voiceId,
      obj.voice_id,
      obj.elevenlabsVoiceId,
      obj.elevenLabsVoiceId,
      obj.elevenlabs_voice_id,
      obj.aiVoiceId,
      obj.ai_voice_id,
      obj.personality?.voiceId,
      obj.personality?.voice_id,
      obj.personality?.elevenlabsVoiceId,
      obj.personality?.elevenlabs_voice_id,
      obj.personality?.elevenlabsVoice?.voiceId,
      obj.personality?.elevenlabsVoice?.voice_id,
      obj.personality?.elevenlabsVoice?.id,
      obj.personality?.elevenLabsVoice?.voiceId,
      obj.personality?.elevenLabsVoice?.voice_id,
      obj.personality?.elevenLabsVoice?.id,
      obj.elevenlabsVoice?.voiceId,
      obj.elevenlabsVoice?.voice_id,
      obj.elevenlabsVoice?.id,
      obj.elevenLabsVoice?.voiceId,
      obj.elevenLabsVoice?.voice_id,
      obj.elevenLabsVoice?.id,
      obj.selectedVoice?.voiceId,
      obj.selectedVoice?.voice_id,
      obj.selectedVoice?.id,
      obj.voice?.voiceId,
      obj.voice?.voice_id,
      obj.voice?.id
    );

    addFromConfig(obj.sessionConfig);
    addFromConfig(obj.configuration);
  };

  sources.forEach(addFromConfig);

  if (typeof window !== "undefined") {
    STORAGE_CONFIG_KEYS.forEach((key) => addFromConfig(safeJsonParse(window.localStorage.getItem(key))));
  }

  return firstCleanString(...candidates);
};

const getApiBaseUrl = () => {
  const env = typeof import.meta !== "undefined" ? import.meta.env || {} : {};
  return String(env.VITE_API_URL || env.VITE_API_BASE_URL || env.VITE_BACKEND_URL || "").replace(/\/+$/, "");
};

const getAuthToken = () => {
  if (typeof window === "undefined") return "";
  for (const key of AUTH_TOKEN_KEYS) {
    const token = window.localStorage.getItem(key);
    if (token) return token;
  }
  const authUser = safeJsonParse(window.localStorage.getItem("user")) || safeJsonParse(window.localStorage.getItem("auth"));
  return firstCleanString(authUser?.access_token, authUser?.accessToken, authUser?.token, authUser?.jwt);
};

const fetchSpeechAudio = async ({ text, voiceId, namespace }) => {
  const cleanText = getQuestionText(text);
  if (!cleanText || !voiceId) return null;

  const token = getAuthToken();
  const response = await fetch(`${getApiBaseUrl()}/api/interview/speech-audio`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      text: cleanText,
      voiceId,
      voice_id: voiceId,
      namespace,
    }),
  });

  if (!response.ok) throw new Error(`Speech audio request failed: ${response.status}`);
  return response.json();
};

/* -------------------- State Machine -------------------- */
const CALL_STATE = {
  IDLE: "idle",
  RINGING: "ringing",
  CONNECTING: "connecting", // greeting in progress
  ACTIVE: "active",
  AI_THINKING: "ai_thinking",
  SUMMARY: "summary",
  ENDING: "ending",
  ANALYZING: "analyzing",
  RESULTS: "results",
};

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/* -------------------- Premium UI Tokens -------------------- */

const GlowBackdrop = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0
    bg-[radial-gradient(55%_45%_at_50%_8%,rgba(108,99,255,0.22),transparent_62%),
        radial-gradient(35%_30%_at_86%_78%,rgba(16,185,129,0.16),transparent_60%),
        radial-gradient(30%_28%_at_14%_82%,rgba(236,72,153,0.10),transparent_62%)]"
  />
);

const HairlineGrid = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0 opacity-30
    [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)]
    [background-size:26px_26px]"
  />
);

const Glass = ({ className = "", children }) => (
  <div
    className={[
      "relative rounded-[28px] border border-white/10",
      "bg-white/[0.045] backdrop-blur-2xl",
      "shadow-[0_20px_60px_rgba(0,0,0,0.55)] overflow-hidden",
      className,
    ].join(" ")}
  >
    <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.10),transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_60%_0%,rgba(255,255,255,0.08),transparent_62%)]" />
    <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#6C63FF]/12 blur-3xl" />
    <div className="absolute -bottom-28 -left-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
    <div className="relative">{children}</div>
  </div>
);

const Chip = ({ icon: Icon, children, tone = "neutral" }) => {
  const map = {
    neutral: "bg-white/5 border-white/10 text-white/75",
    purple: "bg-[#6C63FF]/10 border-[#6C63FF]/20 text-[#D7D4FF]",
    green: "bg-emerald-500/10 border-emerald-500/20 text-emerald-200",
    amber: "bg-amber-500/10 border-amber-500/20 text-amber-200",
    red: "bg-red-500/10 border-red-500/20 text-red-200",
  };
  return (
    <span
      className={[
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold border",
        "tracking-wide",
        map[tone] || map.neutral,
      ].join(" ")}
    >
      {Icon ? <Icon className="h-4 w-4 opacity-90" /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
};

const IconButton = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={[
      "h-12 w-12 rounded-2xl flex items-center justify-center transition",
      "bg-white/5 border border-white/10 text-white/85 hover:bg-white/10",
      "active:translate-y-[1px]",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const PrimaryButton = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={[
      "h-12 px-5 rounded-2xl font-semibold text-white transition",
      "bg-[linear-gradient(135deg,rgba(108,99,255,0.95),rgba(16,185,129,0.55))]",
      "shadow-[0_0_90px_rgba(108,99,255,0.18)] hover:shadow-[0_0_130px_rgba(108,99,255,0.26)]",
      "border border-white/10",
      "active:translate-y-[1px] active:shadow-[0_0_70px_rgba(108,99,255,0.14)]",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const DangerButton = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={[
      "h-12 px-5 rounded-2xl font-semibold transition",
      "bg-red-500/10 border border-red-500/20 text-red-200 hover:bg-red-500/15",
      "shadow-[0_10px_35px_rgba(0,0,0,0.35)]",
      "active:translate-y-[1px]",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const Wave = ({ active, variant = "user" }) => {
  const bars = useMemo(() => Array.from({ length: 22 }, (_, i) => i), []);
  const color =
    variant === "ai" ? "bg-[#6C63FF]/70" : active ? "bg-emerald-400/70" : "bg-white/25";

  return (
    <div className="flex items-end justify-center gap-1.5 h-12">
      {bars.map((i) => {
        const base = variant === "ai" ? 16 : 12;
        const jitter = active ? (variant === "ai" ? 42 : 58) : 8;
        const height = Math.max(
          7,
          Math.round(base + (Math.sin(i * 1.07) + 1) * (jitter / 2))
        );
        const delay = (i % 7) * 35;
        return (
          <div
            key={i}
            className={[
              "w-[6px] rounded-full transition-all duration-200",
              color,
              active ? "animate-pulse" : "",
            ].join(" ")}
            style={{ height, animationDelay: `${delay}ms` }}
          />
        );
      })}
    </div>
  );
};

/* -------------------- iPhone Shell (no image needed) -------------------- */
const IPhoneShell = ({ children, className = "" }) => {
  return (
    <div className={["relative mx-auto w-[calc(100vw-1.5rem)] max-w-[360px] sm:w-[390px] sm:max-w-[390px] select-none", className].join(" ")}>
    <SEO
  title="AI Mock Interview — Practice Interviews Online with Instant Feedback"
  description="Take an AI mock interview online and get real-time feedback on your answers, confidence, clarity, structure, and interview readiness with InterviewGenieAI."
  canonical="https://interviewgenieai.com/ai-mock-interview"
/>
      {/* Outer frame */}
      <div className="relative rounded-[56px] p-[10px] bg-[linear-gradient(180deg,rgba(255,255,255,0.14),rgba(255,255,255,0.06))] shadow-[0_60px_160px_rgba(0,0,0,0.75)]">
        <div className="rounded-[50px] bg-[#05050b] border border-white/10 overflow-hidden">
          {/* Notch */}
          <div className="absolute left-1/2 top-[14px] -translate-x-1/2 z-20">
            <div className="h-[26px] w-[120px] rounded-full bg-black/70 border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]" />
          </div>

          {/* Screen */}
          <div className="relative h-[720px] min-h-[620px] max-h-[calc(100dvh-110px)] sm:h-[820px] sm:max-h-none">
            {/* Screen glow */}
            <div
              aria-hidden
              className="absolute inset-0
              bg-[radial-gradient(700px_520px_at_50%_10%,rgba(108,99,255,0.28),transparent_62%),
                  radial-gradient(520px_420px_at_20%_90%,rgba(16,185,129,0.16),transparent_60%)]"
            />
            <div
              aria-hidden
              className="absolute inset-0 opacity-25
              [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)]
              [background-size:22px_22px]"
            />
            <div className="relative h-full w-full">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------- Phone Screens -------------------- */


const SetupModal = ({ open, onStart, questionsCount = 0 }) => {
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
        e.preventDefault();
        onStart?.();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onStart]);

  if (!open) return null;

  const qc = questionsCount || 5;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto overscroll-contain px-3 py-3 sm:items-center sm:px-4 sm:py-6">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-xl" onClick={onStart} />
      <Glass className="relative my-auto w-full max-w-[720px] max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-[24px] sm:rounded-[28px] [-webkit-overflow-scrolling:touch]">
        <GlowBackdrop />
        <HairlineGrid />
        <div className="p-4 sm:p-8 lg:p-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div>
              <div className="text-xl sm:text-2xl font-semibold text-white tracking-tight">Before we start</div>
              <div className="mt-2 text-sm leading-relaxed text-white/65">
                Set up your space for a clean, high-quality phone interview.
              </div>
            </div>
            <Chip icon={ShieldCheck}>Private</Chip>
          </div>

          <div className="mt-5 sm:mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 sm:p-5">
              <div className="flex items-center gap-2 text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
                <Waves className="h-4 w-4 text-[#D7D4FF]" />
                Environment
              </div>
              <div className="mt-2 text-sm text-white/70">
                Quiet place • stable connection • headphones if possible.
              </div>
            </div>

            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 sm:p-5">
              <div className="flex items-center gap-2 text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
                <Mic className="h-4 w-4 text-emerald-200" />
                Microphone
              </div>
              <div className="mt-2 text-sm text-white/70">
                Allow mic access • speak clearly • keep your phone steady.
              </div>
            </div>

            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 sm:p-5">
              <div className="flex items-center gap-2 text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
                <Captions className="h-4 w-4 text-amber-200" />
                Transcript
              </div>
              <div className="mt-2 text-sm text-white/70">
                Your answer is transcribed live. You can pause anytime.
              </div>
            </div>

            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 sm:p-5">
              <div className="flex items-center gap-2 text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
                <Timer className="h-4 w-4 text-white/70" />
                Structure
              </div>
              <div className="mt-2 text-sm text-white/70">
                One-line summary → one example → measurable outcome.
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-6 rounded-2xl bg-[#0B0B12]/35 border border-white/10 p-4 sm:p-5">
            <div className="flex flex-wrap gap-2">
              <Chip icon={ListChecks}>{qc} questions</Chip>
              <Chip icon={ShieldCheck}>Private session</Chip>
              <Chip icon={Sparkles} tone="purple">AI follow-ups</Chip>
            </div>

            <div className="mt-3 text-sm text-white/70 leading-relaxed">
              Tap to start the incoming call screen. When you press <span className="text-white/85 font-semibold">Answer</span>,
              the ringing stops and the greeting begins. You’ll get the live interview UI right after the greeting ends.
            </div>
          </div>

          <div className="mt-5 sm:mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs text-white/50">
              Tip: If audio is blocked, click once anywhere to unlock sound.
            </div>
            <button type="button" onClick={onStart} style={{
                  boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
                }}
                className="w-full sm:w-auto px-5 py-3 group rounded-xl bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white font-medium transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.4)] flex items-center justify-center gap-2 relative overflow-hidden">
              <div 
                className="absolute -bottom-1 left-[20%] w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px] pointer-events-none"
              />
              Tap to start
            </button>
          </div>
        </div>
      </Glass>
    </div>
  );
};


const PhoneIncomingCall = ({ onDecline, onAnswer }) => {
  return (
    <div className="h-full w-full flex flex-col items-center justify-between pt-16 pb-10 px-8">
      <div className="text-center">
        <div className="text-[12px] font-semibold text-white/55 tracking-[0.24em] uppercase">
          Incoming Call
        </div>

        <div className="mt-10 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-[36px] bg-[#6C63FF]/22 blur-2xl" />
            <img src="/GenieAI_2.png" className="w-32 text-[#D7D4FF]" />
          </div>
        </div>

        <div className="mt-7 text-[34px] font-semibold text-white tracking-tight leading-tight">
          InterviewGenie AI
        </div>
        <div className="mt-2 text-sm text-white/60">
          Senior Recruiter • Structured interview mode
        </div>

        <div className="mt-7 inline-flex items-center gap-2">
          <Chip icon={ShieldCheck} tone="neutral">
            Private Session
          </Chip>
          <Chip icon={Sparkles} tone="purple">
            Follow-ups enabled
          </Chip>
        </div>
      </div>

      {/* Buttons row (Cancel / Answer) */}
      <div className="w-full">
        <div className="grid grid-cols-2 gap-6">
          <button
            type="button"
            onClick={onDecline}
            className="group flex flex-col items-center justify-center gap-3"
          >
            <span className="h-16 w-16 rounded-full bg-red-500/16 border border-red-500/25 flex items-center justify-center shadow-[0_20px_60px_rgba(0,0,0,0.55)] group-hover:bg-red-500/20 transition">
              <PhoneOff className="h-7 w-7 text-red-200" />
            </span>
            <span className="text-[13px] font-semibold text-white/75">Cancel</span>
          </button>

          <button
            type="button"
            onClick={onAnswer}
            className="group flex flex-col items-center justify-center gap-3"
          >
            <span className="relative h-16 w-16 rounded-full bg-emerald-500/18 border border-emerald-500/30 flex items-center justify-center shadow-[0_0_90px_rgba(16,185,129,0.20)] group-hover:bg-emerald-500/20 animate-bounce transition">
              <span className="absolute inset-0 rounded-full bg-emerald-400/15 blur-xl animate-pulse" />
              <Phone className="relative h-7 w-7 text-emerald-100" />
            </span>
            <span className="text-[13px] font-semibold text-white/75">Answer</span>
          </button>
        </div>

        <div className="mt-7 text-center text-[11px] text-white/45">
          Tip: keep answers 60–90s • one example • one measurable result
        </div>
      </div>
    </div>
  );
};


const PhoneGreetingCall = () => {
  return (
    <div className="h-full w-full flex flex-col items-center justify-between pt-16 pb-10 px-8">
      <div className="text-center">
        <div className="text-[12px] font-semibold text-white/55 tracking-[0.24em] uppercase">
          Connected
        </div>

        <div className="mt-10 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-[36px] bg-[#6C63FF]/22 blur-2xl" />
            <img src="/GenieAI_2.png" className="w-28 text-[#D7D4FF]" />
          </div>
        </div>

        <div className="mt-7 text-[28px] font-semibold text-white tracking-tight leading-tight">
          Welcome
        </div>
        <div className="mt-2 text-sm text-white/60">
          Please listen to the greeting. The interview UI will appear right after.
        </div>

        <div className="mt-7">
          <Wave active variant="ai" />
        </div>

        <div className="mt-2 inline-flex items-center gap-2">
          <Chip icon={Sparkles} tone="purple">Greeting</Chip>
          <Chip icon={ShieldCheck}>Private</Chip>
        </div>
      </div>

      <div className="w-full">
        <div className="rounded-[28px] bg-white/[0.055] border border-white/10 p-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(700px_220px_at_10%_0%,rgba(108,99,255,0.12),transparent_60%)]" />
          <div className="relative">
            <div className="text-[11px] font-semibold text-white/55 uppercase tracking-[0.24em] text-center">
              Setup in progress
            </div>
            <div className="mt-2 text-sm text-white/70 text-center">
              We’ll start with question 1 immediately after the greeting ends.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PhoneEndingCall = () => {
  return (
    <div className="h-full w-full flex flex-col items-center justify-between pt-16 pb-10 px-8">
      <div className="text-center">
        <div className="text-[12px] font-semibold text-white/55 tracking-[0.24em] uppercase">
          Ending Call
        </div>

        <div className="mt-10 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-[36px] bg-emerald-500/16 blur-2xl" />
            <img src="/GenieAI_2.png" className="w-28 text-[#D7D4FF]" />
          </div>
        </div>

        <div className="mt-7 text-[28px] font-semibold text-white tracking-tight leading-tight">
          Thank you
        </div>
        <div className="mt-2 text-sm text-white/60">
          Farewell is playing… results will appear right after it finishes.
        </div>

        <div className="mt-7">
          <Wave active variant="ai" />
        </div>

        <div className="mt-2 inline-flex items-center gap-2">
          <Chip icon={ShieldCheck}>Private</Chip>
          <Chip icon={Timer}>Wrapping up</Chip>
        </div>
      </div>

      <div className="w-full">
        <div className="rounded-[28px] bg-white/[0.055] border border-white/10 p-5 text-center">
          <div className="text-sm text-white/70">
            Please don’t close the tab — we’re generating your final score and actions.
          </div>
        </div>
      </div>
    </div>
  );
};


const PhoneLiveCall = ({
  qIndex,
  total,
  micOn,
  isRecording,
  paused,
  question,
  transcript,
  aiThinking,
  onToggleMic,
  onTogglePause,
  onRepeat,
  onEnd,
}) => {
  const statusLeft = aiThinking ? "AI Thinking…" : isRecording ? "Recording" : "Live";
  const statusRight = paused ? "Paused" : micOn ? "Mic on" : "Muted";

  return (
    <div className="h-full w-full flex flex-col pt-16 pb-6">
      {/* Top mini status */}
      <div className="px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className={[
                "inline-flex h-2.5 w-2.5 rounded-full",
                aiThinking ? "bg-[#6C63FF]" : isRecording ? "bg-emerald-400" : "bg-emerald-400",
                "shadow-[0_0_20px_rgba(16,185,129,0.55)]",
              ].join(" ")}
            />
            <span className="text-[11px] font-semibold text-white/70 tracking-widest uppercase">
              {statusLeft}
            </span>
            <span className="ml-2 text-[11px] text-white/50 tabular-nums">
              Q {qIndex + 1}/{total}
            </span>
          </div>
          <div className="text-[11px] text-white/55 tabular-nums">{statusRight}</div>
        </div>

        {/* Identity */}
        <div className="mt-5 flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-[#D7D4FF]" />
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-white truncate">
              InterviewGenie AI
            </div>
            <div className="mt-0.5 text-xs text-white/55 truncate">
              Structured phone interview • Confidence-first coaching
            </div>
          </div>
        </div>
      </div>

      {/* Prompt card */}
      <div className="px-6 mt-6">
        <div className="rounded-[28px] bg-white/[0.055] border border-white/10 p-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(700px_220px_at_10%_0%,rgba(108,99,255,0.12),transparent_60%)]" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
                Current prompt
              </div>
              <Chip tone="neutral">Auto follow-up</Chip>
            </div>
            <div className="mt-3 text-[14px] text-white/85 leading-relaxed line-clamp-4">
              {question}
            </div>

            <div className="mt-4">
              <Wave active={aiThinking || isRecording} variant={aiThinking ? "ai" : "user"} />
              <div className="mt-1 text-center text-[11px] text-white/45">
                Structure → Example → Outcome
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transcript */}
      <div className="px-6 mt-4 flex-1 min-h-0">
        <div className="h-full rounded-[28px] bg-[#05050b]/55 border border-white/10 p-5 overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
              Live transcript
            </div>
            <span className="text-[11px] text-white/45">Auto</span>
          </div>
          <div className="mt-3 text-[12px] text-white/70 leading-relaxed whitespace-pre-wrap">
            {transcript?.trim() ? transcript : "Your response will appear here as you speak…"}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-6 mt-4">
        <div className="rounded-[30px] bg-white/[0.06] border border-white/10 p-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onRepeat}
              className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 text-white/80 flex items-center justify-center active:translate-y-[1px]"
              title="Repeat"
            >
              <RotateCcw className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={onToggleMic}
              className={[
                "h-14 w-14 rounded-[22px] flex items-center justify-center transition active:translate-y-[1px]",
                isRecording
                  ? "bg-[linear-gradient(135deg,rgba(108,99,255,0.95),rgba(16,185,129,0.55))] border border-white/10 shadow-[0_0_90px_rgba(108,99,255,0.26)]"
                  : "bg-white/5 border border-white/10",
              ].join(" ")}
              title={isRecording ? "Stop speaking" : micOn ? "Speak" : "Unmute"}
            >
              {isRecording ? (
                <Square className="h-6 w-6 text-white" />
              ) : micOn ? (
                <Mic className="h-6 w-6 text-white" />
              ) : (
                <MicOff className="h-6 w-6 text-white" />
              )}
            </button>

            <button
              type="button"
              onClick={onEnd}
              className="h-12 w-12 rounded-2xl bg-red-500/12 border border-red-500/20 text-red-200 flex items-center justify-center active:translate-y-[1px]"
              title="End"
            >
              <PhoneOff className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-2 flex items-center justify-between text-[11px] text-white/45">
            <button
              type="button"
              onClick={onTogglePause}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition"
            >
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              {paused ? "Resume" : "Pause"}
            </button>

            <span className="text-[11px] text-white/45">Pause → think → deliver result</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------- Desktop: AI Intelligence Panel -------------------- */

const MetricBar = ({ label, value = 0, hint }) => {
  const v = clamp(Math.round(value), 0, 100);
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
            {label}
          </div>
          {hint ? <div className="mt-1 text-sm text-white/55">{hint}</div> : null}
        </div>
        <div className="text-2xl font-semibold text-white tabular-nums">
          {v}
          <span className="text-sm text-white/55 ml-1">%</span>
        </div>
      </div>

      <div className="mt-4 h-2.5 rounded-full bg-white/5 border border-white/10 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${v}%`,
            backgroundImage:
              "linear-gradient(90deg, rgba(108,99,255,0.90), rgba(16,185,129,0.78))",
          }}
        />
      </div>
    </div>
  );
};

const AIPanel = ({ open, state, time, qIndex, total, metrics, micOn, paused, isRecording, coachHint }) => {
  return (
    <div
      className={[
        "hidden xl:block absolute right-6 top-6 bottom-6 w-[420px] transition-all duration-300",
        open ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6 pointer-events-none",
      ].join(" ")}
    >
      <Glass className="h-full">
        <GlowBackdrop />
        <HairlineGrid />
        <div className="p-6 h-full flex flex-col">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm font-semibold text-white tracking-tight">AI Intelligence</div>
              <div className="mt-1 text-xs text-white/55">
                Real-time coaching signals • trust-first metrics • production-grade
              </div>
            </div>
            <Chip icon={ShieldCheck}>Private</Chip>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Chip icon={Timer}>{formatTime(time)}</Chip>
            <Chip icon={ListChecks}>
              Q {Math.min(qIndex + 1, total)}/{total}
            </Chip>
            <Chip tone={paused ? "amber" : "neutral"} icon={paused ? Pause : Activity}>
              {paused ? "Paused" : "Live"}
            </Chip>
            <Chip tone={micOn ? "green" : "neutral"} icon={micOn ? Mic : MicOff}>
              {micOn ? (isRecording ? "Recording" : "Mic ready") : "Muted"}
            </Chip>
            <Chip tone={state === CALL_STATE.AI_THINKING ? "purple" : "neutral"} icon={Sparkles}>
              {state === CALL_STATE.AI_THINKING ? "AI thinking" : "Signal"}
            </Chip>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4">
            <MetricBar label="Clarity" value={metrics.clarity} hint="Clean structure detected" />
            <MetricBar label="Pacing" value={metrics.pacing} hint="Tempo stability & pauses" />
            <MetricBar label="Confidence" value={metrics.confidence} hint="Intent strength & delivery" />
          </div>

          <div className="mt-5 rounded-2xl bg-[#0B0B12]/35 border border-white/10 p-5">
            <div className="flex items-center gap-2 text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
              <Zap className="h-4 w-4 text-[#D7D4FF]" />
              Coach micro-hint
            </div>
            <div className="mt-2 text-sm text-white/70">
              {coachHint || (
                <>
                  Lead with one-line summary → prove it with <span className="text-white/85 font-semibold">one example</span> → end
                  with measurable outcome.
                </>
              )}
            </div>
          </div>

          <div className="mt-auto pt-5 text-xs text-white/40">
            Premium phone interview • iPhone-first UX • frictionless conversion flow
          </div>
        </div>
      </Glass>
    </div>
  );
};

/* -------------------- LEFT Desktop: Interview Strategy Panel (NEW) -------------------- */

const ChecklistItem = ({ ok, label, meta }) => (
  <div className="flex items-start gap-3">
    <span
      className={[
        "mt-0.5 h-6 w-6 rounded-full border flex items-center justify-center",
        ok ? "bg-emerald-500/10 border-emerald-500/25" : "bg-white/5 border-white/10",
      ].join(" ")}
    >
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-200" /> : <div className="h-2 w-2 rounded-full bg-white/25" />}
    </span>
    <div className="min-w-0">
      <div className="text-sm text-white/80 font-medium leading-snug">{label}</div>
      {meta ? <div className="mt-1 text-xs text-white/55 leading-snug">{meta}</div> : null}
    </div>
  </div>
);


const StrategyPanel = ({ open, question, lastRealtime, transcriptPreview, starSignals, coachChecklist }) => {
  return (
    <div
      className={[
        "hidden xl:block absolute left-6 top-6 bottom-6 w-[420px] transition-all duration-300",
        open ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6 pointer-events-none",
      ].join(" ")}
    >
      <Glass className="h-full">
        <GlowBackdrop />
        <HairlineGrid />

        <div className="p-6 h-full flex flex-col">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm font-semibold text-white tracking-tight">Interview Strategy</div>
              <div className="mt-1 text-xs text-white/55">STAR blueprint • live checklist • premium coaching</div>
            </div>
            <Chip icon={Sparkles} tone="purple">Coach</Chip>
          </div>

          <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex items-center justify-between">
              <div className="text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">STAR blueprint</div>
              <Chip tone="neutral">60–90s</Chip>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3">
              <ChecklistItem ok={!!starSignals.situation} label="S — Situation" meta="Context in 1–2 lines." />
              <ChecklistItem ok={!!starSignals.task} label="T — Task" meta="Your responsibility / goal." />
              <ChecklistItem ok={!!starSignals.action} label="A — Action" meta="What YOU did. Tools, steps, decisions." />
              <ChecklistItem ok={!!starSignals.result} label="R — Result" meta="Outcome + metric (%, time, users, revenue)." />
            </div>

            <div className="mt-4 text-xs text-white/55">
              Current question: <span className="text-white/75 font-semibold">{question || "—"}</span>
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-[#0B0B12]/35 border border-white/10 p-5">
            <div className="flex items-center gap-2 text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
              <Captions className="h-4 w-4 text-white/35" />
              Live checklist
            </div>
            <div className="mt-3 space-y-3">
              {coachChecklist.map((it) => (
                <ChecklistItem key={it.key} ok={it.ok} label={it.label} meta={it.meta} />
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex items-center gap-2 text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
              <Waves className="h-4 w-4 text-[#D7D4FF]" />
              Premium coaching
            </div>
            <div className="mt-2 text-sm text-white/70 leading-relaxed">
              {lastRealtime?.coachHint ||
                "Deliver: 1-line summary → 1 example → measurable result. If you pause, replace fillers with a breath."}
            </div>
            <div className="mt-3 text-xs text-white/45 line-clamp-3">
              {transcriptPreview?.trim() ? `Preview: “${transcriptPreview.trim()}”` : "Preview: start speaking to unlock tailored coaching."}
            </div>
          </div>

          <div className="mt-auto pt-5 text-xs text-white/40">
            Real-time STAR completion updates as you talk.
          </div>
        </div>
      </Glass>
    </div>
  );
};

/* -------------------- Bottom Drawer: Question Summary -------------------- */

const SummaryDrawer = ({ open, onContinue, payload }) => {
  const clarity = payload?.metrics?.clarity ?? 0;
  const pacing = payload?.metrics?.pacing ?? 0;
  const confidence = payload?.metrics?.confidence ?? 0;

  return (
    <div
      className={[
        "fixed left-0 right-0 bottom-0 z-50 transition-all duration-300",
        open ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0 pointer-events-none",
      ].join(" ")}
    >
      <div className="max-w-[1200px] mx-auto px-4 pb-4">
        <Glass className="rounded-[30px]">
          <GlowBackdrop />
          <HairlineGrid />
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2">
                  <Chip icon={Waves} tone="purple">
                    Question Summary
                  </Chip>
                  <Chip icon={ChevronRight}>Auto-scored</Chip>
                </div>
                <div className="mt-2 text-sm text-white/65">Quick feedback before next question. Keep momentum.</div>
              </div>

              <PrimaryButton onClick={onContinue} className="h-12 px-6">
                Continue
              </PrimaryButton>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricBar label="Clarity" value={clarity} hint="Structure & relevance" />
              <MetricBar label="Pacing" value={pacing} hint="Tempo stability" />
              <MetricBar label="Confidence" value={confidence} hint="Delivery strength" />
            </div>

            <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">
                Suggested improvement (next answer)
              </div>
              <div className="mt-2 text-sm text-white/70">
                Finish with: <span className="text-white/85 font-semibold">“This is relevant because…”</span> then connect directly to
                the role.
              </div>
            </div>
          </div>
        </Glass>
      </div>
    </div>
  );
};

/* -------------------- Results (kept, fits the flow) -------------------- */

const ScoreRing = ({ value = 0 }) => {
  const v = clamp(Math.round(value), 0, 100);
  const stroke = 10;
  const r = 44;
  const c = 2 * Math.PI * r;
  const offset = c - (v / 100) * c;

  const tone = v >= 85 ? "text-emerald-300" : v >= 70 ? "text-[#D7D4FF]" : "text-amber-200";

  return (
    <div className="relative h-28 w-28">
      <svg className="h-28 w-28 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} strokeWidth={stroke} className="text-white/10" stroke="currentColor" fill="transparent" />
        <circle
          cx="60"
          cy="60"
          r={r}
          strokeWidth={stroke}
          className="text-white"
          stroke="currentColor"
          fill="transparent"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ filter: "drop-shadow(0 0 14px rgba(108,99,255,0.22))" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center flex-col">
        <div className={`text-3xl font-semibold ${tone} tabular-nums`}>{v}</div>
        <div className="text-[11px] font-semibold text-white/55 tracking-widest uppercase">Score</div>
      </div>
    </div>
  );
};

/* -------------------- PDF Report Generator (print-to-PDF) -------------------- */
/* Opens a beautifully-styled report in a new window and triggers browser print
 * dialog so user can "Save as PDF". Zero dependencies.
 *
 * Replace placeholder images in /public with your real assets:
 *   - /public/report-logo.png        (top-left logo, ~120x40)
 *   - /public/report-hero.jpg        (hero banner image, ~1200x300)
 *   - /public/report-signature.png   (footer signature mark, ~140x60)
 */
const generateInterviewPDFReport = ({ score, answers, totalTime, candidateName = "Candidate" }) => {
  const v = (n) => Math.max(0, Math.min(100, Math.round(n || 0)));
  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const x = s % 60;
    return `${m}m ${String(x).padStart(2, "0")}s`;
  };
  const today = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const clarity = Math.round(answers.reduce((a, b) => a + (b.metrics?.clarity ?? 0), 0) / Math.max(1, answers.length));
  const pacing = Math.round(answers.reduce((a, b) => a + (b.metrics?.pacing ?? 0), 0) / Math.max(1, answers.length));
  const confidence = Math.round(answers.reduce((a, b) => a + (b.metrics?.confidence ?? 0), 0) / Math.max(1, answers.length));

  const weak = [];
  if (clarity < 70) weak.push("Clarity could be sharper — start with a one-line headline answer before details.");
  if (pacing < 70) weak.push("Pacing was uneven — practice steady delivery with deliberate micro-pauses.");
  if (confidence < 70) weak.push("Confidence dipped — reduce hedging words ('maybe', 'I think') and end statements firmly.");
  if (!weak.length) weak.push("Keep refining: tighter examples and stronger results-oriented closings.");

  const scoreColor = score >= 85 ? "#10b981" : score >= 70 ? "#6c63ff" : "#f59e0b";
  const scoreBand = score >= 85 ? "Excellent" : score >= 70 ? "Strong" : score >= 55 ? "Developing" : "Needs Work";

  const reportHTML = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>Interview Performance Report — ${candidateName}</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body {
    margin: 0; padding: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #1a1a2e; background: #f7f7fb;
  }
  .page {
    width: 210mm; min-height: 297mm; margin: 0 auto;
    background: #ffffff; padding: 0; position: relative;
    page-break-after: always;
  }
  .page:last-child { page-break-after: auto; }

  /* ── Header ── */
  .header {
    background: linear-gradient(135deg, #6c63ff 0%, #8b5cf6 50%, #ec4899 100%);
    padding: 24px 36px; color: #ffffff; position: relative; overflow: hidden;
  }
  .header::after {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(800px 300px at 80% -20%, rgba(255,255,255,0.18), transparent 60%);
    pointer-events: none;
  }
  .header-row { display: flex; justify-content: space-between; align-items: center; position: relative; }
  .logo-block { display: flex; align-items: center; gap: 12px; }
  .logo-img { height: 36px; width: auto; filter: brightness(0) invert(1); }
  .logo-fallback {
    height: 36px; width: 36px; border-radius: 10px;
    background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 16px; letter-spacing: -0.5px;
  }
  .brand-name { font-size: 14px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; opacity: 0.95; }
  .brand-tag { font-size: 10px; opacity: 0.75; margin-top: 2px; letter-spacing: 1px; }
  .header-meta { text-align: right; font-size: 11px; opacity: 0.9; line-height: 1.6; }

  /* ── Hero image strip ── */
  .hero {
    height: 110px; background: #1a1a2e;
    background-image: linear-gradient(135deg, rgba(108,99,255,0.55), rgba(16,185,129,0.45)), url('/assets/images/technical.png');
    background-size: cover; background-position: center;
    position: relative;
  }
  .hero-overlay {
    position: absolute; inset: 0; padding: 0 36px;
    display: flex; align-items: center; justify-content: space-between; color: #fff;
  }
  .hero-title { font-size: 24px; font-weight: 700; letter-spacing: -0.5px; }
  .hero-sub { font-size: 12px; opacity: 0.85; margin-top: 4px; }
  .hero-date {
    font-size: 11px; padding: 6px 14px; background: rgba(255,255,255,0.18);
    border-radius: 999px; backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.25);
  }

  /* ── Body ── */
  .body { padding: 28px 36px; }

  .candidate-card {
    display: flex; gap: 24px; align-items: center; padding: 20px 24px;
    background: linear-gradient(135deg, #fafafe 0%, #f0eefe 100%);
    border: 1px solid rgba(108,99,255,0.18); border-radius: 16px; margin-bottom: 28px;
  }
  .candidate-info h2 { margin: 0; font-size: 22px; color: #1a1a2e; letter-spacing: -0.3px; }
  .candidate-info p  { margin: 4px 0 0 0; font-size: 12px; color: #6b6b85; }

  /* ── Score ring ── */
  .score-block { display: flex; gap: 24px; margin-bottom: 28px; }
  .score-ring {
    width: 130px; height: 130px; flex-shrink: 0; position: relative;
  }
  .score-ring svg { width: 100%; height: 100%; transform: rotate(-90deg); }
  .score-ring .score-text {
    position: absolute; inset: 0; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
  }
  .score-ring .score-num { font-size: 36px; font-weight: 800; letter-spacing: -1px; color: ${scoreColor}; }
  .score-ring .score-label { font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #6b6b85; }
  .score-detail { flex: 1; }
  .score-detail h3 { margin: 0 0 6px 0; font-size: 18px; color: #1a1a2e; letter-spacing: -0.3px; }
  .score-band {
    display: inline-block; padding: 4px 12px; border-radius: 999px;
    background: ${scoreColor}1f; color: ${scoreColor}; font-size: 11px; font-weight: 700;
    letter-spacing: 0.5px; margin-bottom: 10px;
  }
  .score-detail p { margin: 0; font-size: 12px; color: #4a4a66; line-height: 1.6; }

  /* ── Metric grid ── */
  .metrics { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 28px; }
  .metric {
    border: 1px solid #e5e5ee; border-radius: 14px; padding: 14px 16px;
    background: #ffffff;
  }
  .metric-label { font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #6b6b85; }
  .metric-value { font-size: 26px; font-weight: 800; color: #1a1a2e; margin-top: 6px; letter-spacing: -1px; }
  .metric-value span { font-size: 13px; color: #6b6b85; font-weight: 600; }
  .metric-bar { height: 6px; background: #f0f0f7; border-radius: 999px; margin-top: 10px; overflow: hidden; }
  .metric-fill {
    height: 100%; border-radius: 999px;
    background: linear-gradient(90deg, #6c63ff, #10b981);
  }

  /* ── Two-column section ── */
  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 28px; }
  .col-card {
    border-radius: 14px; padding: 18px 20px; border: 1px solid #e5e5ee; background: #ffffff;
  }
  .col-card.green { border-color: rgba(16,185,129,0.25); background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%); }
  .col-card.amber { border-color: rgba(245,158,11,0.25); background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); }
  .col-title { font-size: 13px; font-weight: 700; margin: 0 0 12px 0; display: flex; align-items: center; gap: 8px; }
  .green .col-title { color: #047857; }
  .amber .col-title { color: #92400e; }
  .icon-dot {
    width: 22px; height: 22px; border-radius: 7px; display: inline-flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800;
  }
  .green .icon-dot { background: rgba(16,185,129,0.18); color: #047857; }
  .amber .icon-dot { background: rgba(245,158,11,0.20); color: #b45309; }
  .col-card ul { padding-left: 0; margin: 0; list-style: none; }
  .col-card li { font-size: 11.5px; line-height: 1.55; color: #2c2c44; margin-bottom: 8px; padding-left: 16px; position: relative; }
  .col-card li::before {
    content: ''; position: absolute; left: 0; top: 7px;
    width: 5px; height: 5px; border-radius: 50%;
  }
  .green li::before { background: #10b981; }
  .amber li::before { background: #f59e0b; }

  /* ── Question breakdown table ── */
  .qb-section { margin-bottom: 24px; }
  .section-title {
    font-size: 14px; font-weight: 700; color: #1a1a2e; margin: 0 0 14px 0;
    padding-bottom: 8px; border-bottom: 2px solid #6c63ff;
    display: inline-block;
  }
  .qb-item {
    border: 1px solid #e5e5ee; border-radius: 12px; padding: 14px 18px; margin-bottom: 10px;
    background: #ffffff; page-break-inside: avoid;
  }
  .qb-head {
    display: flex; justify-content: space-between; align-items: flex-start; gap: 14px;
    margin-bottom: 8px;
  }
  .qb-num {
    background: linear-gradient(135deg, #6c63ff, #8b5cf6);
    color: #fff; font-size: 10px; font-weight: 800;
    padding: 4px 10px; border-radius: 6px; letter-spacing: 0.5px;
    flex-shrink: 0;
  }
  .qb-q { font-size: 12px; font-weight: 600; color: #1a1a2e; line-height: 1.4; }
  .qb-a { font-size: 11px; color: #6b6b85; line-height: 1.5; margin: 6px 0 10px 0; padding: 8px 12px;
    background: #f7f7fb; border-radius: 8px; border-left: 3px solid #6c63ff; }
  .qb-stats { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
  .qb-stat {
    background: #f7f7fb; border-radius: 8px; padding: 8px 10px;
  }
  .qb-stat-label { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #6b6b85; }
  .qb-stat-val { font-size: 13px; font-weight: 700; color: #1a1a2e; margin-top: 2px; }

  /* ── Action card ── */
  .actions { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 28px; }
  .action {
    background: linear-gradient(135deg, #1a1a2e 0%, #2d2d4f 100%);
    color: #fff; border-radius: 14px; padding: 16px 18px;
  }
  .action-num {
    display: inline-block; width: 24px; height: 24px; border-radius: 7px;
    background: rgba(255,255,255,0.15); font-size: 11px; font-weight: 800;
    text-align: center; line-height: 24px; margin-bottom: 10px;
  }
  .action h4 { margin: 0 0 6px 0; font-size: 13px; }
  .action p  { margin: 0; font-size: 11px; opacity: 0.8; line-height: 1.5; }

  /* ── Footer ── */
  .footer {
    margin-top: 30px; padding: 20px 36px; background: #f7f7fb;
    border-top: 1px solid #e5e5ee;
    display: flex; align-items: center; justify-content: space-between;
  }
  .footer-left { font-size: 10px; color: #6b6b85; line-height: 1.6; }
  .footer-left strong { color: #1a1a2e; font-size: 11px; }
  .footer-sig img { height: 40px; opacity: 0.7; }
  .footer-sig-fallback {
    font-size: 10px; color: #6b6b85; font-style: italic;
    border-top: 1px solid #aaa; padding-top: 4px; min-width: 120px; text-align: center;
  }

  @media print {
    body { background: #ffffff; }
    .page { box-shadow: none; }
    .no-print { display: none !important; }
  }

  /* On-screen "Save PDF" floating bar */
  .toolbar {
    position: fixed; top: 16px; right: 16px; z-index: 9999;
    background: #1a1a2e; color: #fff; padding: 10px 14px; border-radius: 12px;
    display: flex; gap: 10px; box-shadow: 0 10px 30px rgba(0,0,0,0.3);
    font-family: -apple-system, sans-serif;
  }
  .toolbar button {
    background: linear-gradient(135deg, #6c63ff, #8b5cf6);
    color: #fff; border: 0; padding: 8px 16px; border-radius: 8px;
    font-weight: 600; cursor: pointer; font-size: 13px;
  }
  .toolbar button.secondary { background: rgba(255,255,255,0.1); }
</style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">Save as PDF</button>
    <button class="secondary" onclick="window.close()">Close</button>
  </div>

  <div class="page">
    <!-- Header -->
    <div class="header">
      <div class="header-row">
        <div class="logo-block">
          <img src="/GenieAI_2.png" class="logo-img" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'" />
          <div class="logo-fallback" style="display:none">IG</div>
          <div>
            <div class="brand-name">InterviewGenie AI</div>
            <div class="brand-tag">Performance Intelligence</div>
          </div>
        </div>
        <div class="header-meta">
          <div>Report ID: <strong>${Math.random().toString(36).slice(2, 10).toUpperCase()}</strong></div>
          <div>Generated: ${today}</div>
        </div>
      </div>
    </div>

    <!-- Hero -->
    <div class="hero">
      <div class="hero-overlay">
        <div>
          <div class="hero-title">Interview Performance Report</div>
          <div class="hero-sub">Confidential candidate evaluation</div>
        </div>
        <div class="hero-date">${today}</div>
      </div>
    </div>

    <!-- Body -->
    <div class="body">
      <!-- Candidate -->
      <div class="candidate-card">
        <div style="width:56px;height:56px;border-radius:14px;background:linear-gradient(135deg,#6c63ff,#ec4899);display:flex;align-items:center;justify-content:center;color:#fff;font-size:22px;font-weight:800">
          ${candidateName.slice(0, 1).toUpperCase()}
        </div>
        <div class="candidate-info">
          <h2>${candidateName}</h2>
          <p>Interview duration: ${fmt(totalTime)} · ${answers.length} questions answered · Phone format</p>
        </div>
      </div>

      <!-- Score block -->
      <div class="score-block">
        <div class="score-ring">
          <svg viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" stroke="#f0f0f7" stroke-width="10" fill="none" />
            <circle cx="60" cy="60" r="50" stroke="${scoreColor}" stroke-width="10" fill="none"
              stroke-linecap="round"
              stroke-dasharray="${2 * Math.PI * 50}"
              stroke-dashoffset="${2 * Math.PI * 50 * (1 - score / 100)}" />
          </svg>
          <div class="score-text">
            <div class="score-num">${score}</div>
            <div class="score-label">Overall</div>
          </div>
        </div>
        <div class="score-detail">
          <h3>Performance Verdict</h3>
          <div class="score-band">${scoreBand} performance</div>
          <p>This score reflects clarity, pacing, and confidence across ${answers.length} responses. Detailed breakdown and improvement actions are listed below. Keep practicing the highlighted areas to push your next session above 85.</p>
        </div>
      </div>

      <!-- Metrics -->
      <div class="metrics">
        <div class="metric">
          <div class="metric-label">Clarity</div>
          <div class="metric-value">${clarity}<span>/100</span></div>
          <div class="metric-bar"><div class="metric-fill" style="width:${clarity}%"></div></div>
        </div>
        <div class="metric">
          <div class="metric-label">Pacing</div>
          <div class="metric-value">${pacing}<span>/100</span></div>
          <div class="metric-bar"><div class="metric-fill" style="width:${pacing}%"></div></div>
        </div>
        <div class="metric">
          <div class="metric-label">Confidence</div>
          <div class="metric-value">${confidence}<span>/100</span></div>
          <div class="metric-bar"><div class="metric-fill" style="width:${confidence}%"></div></div>
        </div>
      </div>

      <!-- Strengths / Improvements -->
      <div class="two-col">
        <div class="col-card green">
          <div class="col-title"><span class="icon-dot">✓</span> Strengths</div>
          <ul>
            <li>Clear structure when delivering one example with a defined outcome.</li>
            <li>Confidence increases when pacing stays steady with deliberate micro-pauses.</li>
            <li>Strong relevance when closing with a "why it matters" tied to the role.</li>
          </ul>
        </div>
        <div class="col-card amber">
          <div class="col-title"><span class="icon-dot">!</span> Areas to Improve</div>
          <ul>
            ${weak.map((w) => `<li>${w}</li>`).join("")}
          </ul>
        </div>
      </div>

      <!-- Question breakdown -->
      <div class="qb-section">
        <h3 class="section-title">Question-by-Question Breakdown</h3>
        ${answers
          .map(
            (a, i) => `
          <div class="qb-item">
            <div class="qb-head">
              <span class="qb-num">Q${i + 1}</span>
              <div class="qb-q">${(a.question || "—").replace(/</g, "&lt;")}</div>
            </div>
            <div class="qb-a">${(a.transcript || "No answer recorded").replace(/</g, "&lt;").slice(0, 360)}${(a.transcript || "").length > 360 ? "…" : ""}</div>
            <div class="qb-stats">
              <div class="qb-stat">
                <div class="qb-stat-label">Clarity</div>
                <div class="qb-stat-val">${v(a.metrics?.clarity)}%</div>
              </div>
              <div class="qb-stat">
                <div class="qb-stat-label">Pacing</div>
                <div class="qb-stat-val">${v(a.metrics?.pacing)}%</div>
              </div>
              <div class="qb-stat">
                <div class="qb-stat-label">Confidence</div>
                <div class="qb-stat-val">${v(a.metrics?.confidence)}%</div>
              </div>
            </div>
          </div>
        `
          )
          .join("")}
      </div>

      <!-- Next actions -->
      <h3 class="section-title">Recommended Next Actions</h3>
      <div class="actions">
        <div class="action">
          <div class="action-num">1</div>
          <h4>30-Second Opener</h4>
          <p>Practice: role → impact → tools → why now. Memorize the structure, freestyle the words.</p>
        </div>
        <div class="action">
          <div class="action-num">2</div>
          <h4>STAR Drills</h4>
          <p>Pick three stories. Train Situation → Task → Action → Result until each lasts 60–90s.</p>
        </div>
        <div class="action">
          <div class="action-num">3</div>
          <h4>Close Strong</h4>
          <p>End every answer with "This is relevant because…" and tie it back to the role.</p>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer">
      <div class="footer-left">
        <strong>InterviewGenie AI</strong><br />
        Confidential — generated for ${candidateName} on ${today}.<br />
        This report reflects performance signals from a single session. Use it to guide focused practice.
      </div>
      <div class="footer-sig">
        <img src="/report-signature.png" onerror="this.style.display='none';this.nextElementSibling.style.display='block'" />
        <div class="footer-sig-fallback" style="display:none">Authorized Report</div>
      </div>
    </div>
  </div>

  <script>
    // Auto-trigger print dialog after assets settle
    setTimeout(() => { window.focus(); window.print(); }, 600);
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) {
    alert("Please allow popups to download your interview report as PDF.");
    return;
  }
  w.document.open();
  w.document.write(reportHTML);
  w.document.close();
};

const ResultsScreen = ({ score, answers, totalTime, onRestart, onExit }) => {
  const resultsRef = useRef(null);

  // Auto-scroll to top of results when component mounts
  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const top = [
    { title: "Clarity", icon: TrendingUp, value: avg(answers, "clarity") },
    { title: "Pacing", icon: AudioLines, value: avg(answers, "pacing") },
    { title: "Confidence", icon: Award, value: avg(answers, "confidence") },
  ];

  const weaknesses = deriveWeaknesses(answers);

  return (
    <div ref={resultsRef} className="min-h-[calc(100vh-64px)] w-full flex items-center justify-center px-4 py-10">
      <Glass className="w-full max-w-[1100px]">
        <GlowBackdrop />
        <HairlineGrid />
        <div className="p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 flex-wrap">
                <Chip icon={BarChart3} tone="purple">
                  Results
                </Chip>
                <Chip icon={Timer}>Total: {formatTime(totalTime)}</Chip>
                <Chip icon={ListChecks}>{answers.length} answers</Chip>
              </div>

              <div className="mt-4 text-3xl sm:text-4xl font-semibold text-white tracking-tight">Interview Performance Summary</div>
              <div className="mt-2 text-sm text-white/65 max-w-2xl">
                What you did well, what needs improvement, and exactly what to do next.
              </div>
            </div>

            <div className="flex items-center gap-6">
              <ScoreRing value={score} />
              <div className="space-y-2">
                <PrimaryButton
                  onClick={() => generateInterviewPDFReport({ score, answers, totalTime })}
                  className="w-full lg:w-auto"
                >
                  <span className="inline-flex items-center gap-2">
                    <Download className="h-5 w-5" />
                    Download Report
                  </span>
                </PrimaryButton>

                <div className="flex gap-2">
                  <PrimaryButton
                    onClick={onRestart}
                    className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 shadow-none"
                  >
                    <span className="inline-flex items-center gap-2">
                      <RefreshCcw className="h-4 w-4" />
                      Retry
                    </span>
                  </PrimaryButton>
                  <DangerButton onClick={onExit} className="flex-1">
                    Exit
                  </DangerButton>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            {top.map((k) => (
              <div key={k.title} className="rounded-2xl bg-white/5 border border-white/10 p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">{k.title}</div>
                  <k.icon className="h-4 w-4 text-white/35" />
                </div>
                <div className="mt-3 text-3xl font-semibold text-white tabular-nums tracking-tight">
                  {k.value}
                  <span className="text-sm text-white/55 ml-1">%</span>
                </div>
                <div className="mt-3 h-2.5 rounded-full bg-white/5 border border-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${k.value}%`,
                      backgroundImage:
                        "linear-gradient(90deg, rgba(108,99,255,0.85), rgba(16,185,129,0.75))",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-[#0B0B12]/35 border border-white/10 p-6">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <CheckCircle2 className="h-5 w-5 text-emerald-200" />
                Strengths
              </div>
              <ul className="mt-4 space-y-3 text-sm text-white/75">
                <li className="flex gap-2">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-200" />
                  </span>
                  Strong structure when you deliver one clear example and outcome.
                </li>
                <li className="flex gap-2">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-200" />
                  </span>
                  Confidence increases when pacing stays steady with micro-pauses.
                </li>
                <li className="flex gap-2">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-200" />
                  </span>
                  Clear relevance when you close with “why it matters” to the role.
                </li>
              </ul>
            </div>

            <div className="rounded-2xl bg-[#0B0B12]/35 border border-white/10 p-6">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <AlertTriangle className="h-5 w-5 text-amber-200" />
                Improvements
              </div>
              <ul className="mt-4 space-y-3 text-sm text-white/75">
                {weaknesses.map((w, idx) => (
                  <li key={idx} className="flex gap-2">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-200" />
                    </span>
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="text-sm font-semibold text-white tracking-tight">Question Breakdown</div>
              <Chip tone="neutral">Per answer signals</Chip>
            </div>

            <div className="divide-y divide-white/10">
              {answers.map((a, i) => (
                <div key={i} className="px-6 py-5 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-5">
                  <div className="min-w-0">
                    <div className="text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">Q{i + 1}</div>
                    <div className="mt-2 text-base text-white/85 leading-snug">{a.question}</div>
                    <div className="mt-3 text-sm text-white/60 line-clamp-2">{a.transcript || "—"}</div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <MiniStat label="Clarity" value={a.metrics?.clarity ?? 0} />
                    <MiniStat label="Pacing" value={a.metrics?.pacing ?? 0} />
                    <MiniStat label="Confidence" value={a.metrics?.confidence ?? 0} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-2xl bg-[#0B0B12]/35 border border-white/10 p-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-white">
              <Sparkles className="h-5 w-5 text-[#D7D4FF]" />
              Next Actions (fast improvement)
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <ActionCard title="30s opener" desc="Practice: role → impact → tools → why now." />
              <ActionCard title="STAR drills" desc="Pick 3 stories. Train Situation → Task → Action → Result." />
              <ActionCard title="Close strong" desc='End with: “This is relevant because…” + tie to role.' />
            </div>
          </div>
        </div>
      </Glass>
    </div>
  );
};

const MiniStat = ({ label, value }) => {
  const v = clamp(Math.round(value || 0), 0, 100);
  return (
    <div className="rounded-2xl bg-black/20 border border-white/10 p-4">
      <div className="text-[10px] sm:text-[11px] font-semibold text-white/55 uppercase tracking-[0.16em] sm:tracking-[0.24em]">{label}</div>
      <div className="mt-2 text-xl font-semibold text-white tabular-nums tracking-tight">
        {v}
        <span className="text-xs text-white/55 ml-1">%</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${v}%`,
            backgroundImage:
              "linear-gradient(90deg, rgba(16,185,129,0.9), rgba(108,99,255,0.8))",
          }}
        />
      </div>
    </div>
  );
};

const ActionCard = ({ title, desc }) => (
  <div className="rounded-2xl bg-white/5 border border-white/10 p-4 sm:p-5">
    <div className="text-sm font-semibold text-white tracking-tight">{title}</div>
    <div className="mt-2 text-sm text-white/65">{desc}</div>
  </div>
);

function avg(answers, key) {
  if (!answers?.length) return 0;
  const sum = answers.reduce((acc, a) => acc + (a.metrics?.[key] ?? 0), 0);
  return clamp(Math.round(sum / answers.length), 0, 100);
}

function deriveWeaknesses(answers) {
  const clarity = avg(answers, "clarity");
  const pacing = avg(answers, "pacing");
  const conf = avg(answers, "confidence");

  const items = [];
  if (clarity < 75) items.push("Improve clarity: 1-line summary → example → measurable outcome.");
  if (pacing < 70) items.push("Pacing: slow down slightly and pause after key points.");
  if (conf < 75) items.push("Confidence: state outcomes clearly and quantify results.");
  if (!items.length) items.push("Strong overall. Next: sharpen your closing statement for impact.");
  return items;
}

/* -------------------- Backend helpers (swapped to your JS services) -------------------- */

function pickConfidenceFromAnalysis(resp) {
  // Prefer AI validated tone if present, else local tone, else fallback.
  const aiTone = resp?.ai_analysis?.validated_metrics?.tone;
  const localTone = resp?.local_analysis?.metrics?.tone;
  const tone = typeof aiTone === "number" ? aiTone : typeof localTone === "number" ? localTone : 0;
  return clamp(Math.round(tone), 0, 100);
}

function buildCoachHintFromMetrics(m) {
  if (!m) return "";
  const clarity = m.clarity ?? 0;
  const pacing = m.pacing ?? 0;
  const conf = m.confidence ?? 0;

  const weakest = [
    { k: "clarity", v: clarity },
    { k: "pacing", v: pacing },
    { k: "confidence", v: conf },
  ].sort((a, b) => a.v - b.v)[0];

  if (weakest.k === "clarity")
    return "Clarity: open with a 1-line summary → 1 example → 1 measurable outcome. Avoid fillers; use a pause.";
  if (weakest.k === "pacing") return "Pacing: slow down ~10% and insert a micro-pause after each key point.";
  return "Confidence: use assertive verbs (led/owned/delivered) + quantify results (%, time, users, revenue).";
}

function computeSTARSignals(transcript = "", aiStar = null) {
  // Use AI STAR flags when available, else light heuristic.
  const t = (transcript || "").toLowerCase();
  const hasNumbers = /\d+/.test(t);

  const heuristic = {
    situation: /\b(situation|context|at (my|the) (company|team)|when i|during)\b/.test(t) || t.length > 40,
    task: /\b(task|goal|responsible|objective|needed to|my role)\b/.test(t),
    action: /\b(i did|i built|i implemented|i created|i led|i managed|i designed|we|my approach)\b/.test(t),
    result: /\b(result|impact|outcome|improved|increased|reduced|saved|delivered|launched)\b/.test(t) || hasNumbers,
  };

  const ai = aiStar
    ? {
        situation: !!aiStar.situation,
        task: !!aiStar.task,
        action: !!aiStar.action,
        result: !!aiStar.result,
      }
    : null;

  return {
    situation: ai ? ai.situation : heuristic.situation,
    task: ai ? ai.task : heuristic.task,
    action: ai ? ai.action : heuristic.action,
    result: ai ? ai.result : heuristic.result,
  };
}

/* -------------------- Main Component (KEEP DESIGN, FIX LOGIC) -------------------- */

export default function PhoneInterviewTest() {
  const cfg = useMemo(() => loadConfiguration(), []);
  const sessionConfig = cfg?.sessionConfig;
  const configuredJobRole = cfg?.jobRole || "";
  const configuredDifficulty = cfg?.difficulty || "intermediate";
  const configuredLanguage = cfg?.language || "en-US";
  const configuredVoiceGender = cfg?.voiceGender || "female";
  const configuredVoiceId = useMemo(() => extractVoiceId(cfg, sessionConfig), [cfg, sessionConfig]);
  const sessionConfigWithVoice = useMemo(() => {
    if (!sessionConfig) return sessionConfig;
    if (!configuredVoiceId) return sessionConfig;
    return {
      ...sessionConfig,
      voiceId: configuredVoiceId,
      voice_id: configuredVoiceId,
      elevenlabsVoiceId: configuredVoiceId,
      elevenlabs_voice_id: configuredVoiceId,
    };
  }, [sessionConfig, configuredVoiceId]);

  const [showConfetti,setShowConfetti]=useState(false);

  // Setup modal blocks audio until the user clicks once (best UX + autoplay unlock)
  const [setupOpen, setSetupOpen] = useState(true);

  const [state, setState] = useState(CALL_STATE.RINGING);

  const [paused, setPaused] = useState(false);
  const [callTime, setCallTime] = useState(0);

  const [questions, setQuestions] = useState(MOCK_QUESTIONS);
  const [qIndex, setQIndex] = useState(0);
  const [question, setQuestion] = useState(MOCK_QUESTIONS[0]);

  const [micOn, setMicOn] = useState(true);
  const [isRecording, setIsRecording] = useState(false);

  const [liveTranscript, setLiveTranscript] = useState("");
  const [transcript, setTranscript] = useState("");

  const [answers, setAnswers] = useState([]);
  const [score, setScore] = useState(0);

  const answersRef = useRef([]);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // UI expects clarity/pacing/confidence — we map confidence from backend tone.
  const [metrics, setMetrics] = useState({ clarity: 0, pacing: 0, confidence: 0 });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastAnswerPayload, setLastAnswerPayload] = useState(null);

  // For strategy/coach panel
  const [lastRealtime, setLastRealtime] = useState(null);
  const [coachHint, setCoachHint] = useState("");

  const timerRef = useRef(null);

  // Ringtone + call audio cues (logic only; no UI changes)
  const ringtoneRef = useRef(null);
  const generatedSpeechAudioRef = useRef(null);
  const setupOpenRef = useRef(true);
  useEffect(() => {
    setupOpenRef.current = setupOpen;
  }, [setupOpen]);

  // Greeting must finish before showing the live interview UI
  const greetingSpokenRef = useRef(false);
  const lastSpokenQuestionRef = useRef("");

  // Speech recognition refs
  const recRef = useRef(null);
  const isFirstSpeechRef = useRef(false);
  const questionShownAtRef = useRef(Date.now());
  const recordingStartedAtRef = useRef(0);
  const answerStartTimeRef = useRef(0);
  const delaySecondsRef = useRef(0);
  const latestTextRef = useRef("");

  // realtime analysis throttle
  const analyzeThrottleRef = useRef({ lastAt: 0, pending: false });
  const abortRef = useRef(null);
  const lastAnalysisTimeRef = useRef(0);

  // Speech service state (kept separate from UI call state)
  const [interviewState, setInterviewState] = useState("idle");
  const [waitingForStart, setWaitingForStart] = useState(false);
  const isRecordingRef = useRef(false);
  const interviewStateRef = useRef("idle");
  const waitingForStartRef = useRef(false);
  const callStateRef = useRef(state);
  const transcriptRef = useRef("");
  
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);
  useEffect(() => {
    interviewStateRef.current = interviewState;
  }, [interviewState]);
  useEffect(() => {
    waitingForStartRef.current = waitingForStart;
  }, [waitingForStart]);
  useEffect(() => {
    callStateRef.current = state;
  }, [state]);

  const total = questions.length;


  const stopRingtone = () => {
    try {
      const a = ringtoneRef.current;
      if (!a) return;
      a.pause();
      a.currentTime = 0;
    } catch {
      // ignore
    }
  };

  const stopGeneratedSpeechAudio = useCallback(() => {
    try {
      const audio = generatedSpeechAudioRef.current;
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
      audio.onended = null;
      audio.onerror = null;
      generatedSpeechAudioRef.current = null;
    } catch {
      // ignore
    }
  }, []);

  const startGeneratedSpeechAudio = useCallback(
    async (audioUrl, onDone) => {
      if (!audioUrl) return false;

      try {
        stopGeneratedSpeechAudio();
        const audio = new Audio(audioUrl);
        audio.preload = "auto";
        const finish = () => {
          if (generatedSpeechAudioRef.current === audio) {
            generatedSpeechAudioRef.current = null;
          }
          onDone?.();
        };
        audio.onended = finish;
        audio.onerror = finish;
        generatedSpeechAudioRef.current = audio;
        await audio.play();
        return true;
      } catch {
        stopGeneratedSpeechAudio();
        return false;
      }
    },
    [stopGeneratedSpeechAudio]
  );

  const speakInterviewText = useCallback(
    (textOrItem, namespace = "runtime") =>
      new Promise(async (resolve) => {
        const text = getQuestionText(textOrItem);
        if (!text) {
          resolve();
          return;
        }

        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };

        const directAudioUrl =
          typeof textOrItem === "object" && textOrItem
            ? textOrItem.audio_url || textOrItem.audioUrl || textOrItem.audio
            : "";

        try {
          stopSpeaking();
        } catch {
          // ignore
        }

        if (directAudioUrl) {
          const started = await startGeneratedSpeechAudio(directAudioUrl, finish);
          if (started) return;
        }

        if (configuredVoiceId) {
          try {
            const meta = await fetchSpeechAudio({
              text,
              voiceId: configuredVoiceId,
              namespace,
            });

            const audioUrl = meta?.audio_url || meta?.audioUrl;
            const started = await startGeneratedSpeechAudio(audioUrl, finish);
            if (started) return;
          } catch {
            // Fallback below keeps the call flow working even if ElevenLabs/R2 fails.
          }
        }

        try {
          speakText(text, configuredLanguage, configuredVoiceGender, finish);
        } catch {
          finish();
        }
      }),
    [configuredLanguage, configuredVoiceGender, configuredVoiceId, startGeneratedSpeechAudio]
  );

  const stopAllSpeech = useCallback(() => {
    stopGeneratedSpeechAudio();
    try {
      stopSpeaking();
    } catch {
      // ignore
    }
  }, [stopGeneratedSpeechAudio]);

  // startExperience: called from the setup modal button (inside a user gesture → autoplay-safe)
  const startExperience = () => {
    if (!setupOpenRef.current) return;
    setupOpenRef.current = false;
    setSetupOpen(false);

    if (callStateRef.current === CALL_STATE.RINGING) {
      // Create and play within the user gesture so browsers allow autoplay
      if (!ringtoneRef.current) {
        const a = new Audio();
        a.loop = true;
        a.volume = 0.6;
        const candidates = ["/audio/iphon_13_original.mp3", "/audio/iphon_original.mp3"];
        let idx = 0;
        const tryNext = () => {
          if (idx >= candidates.length) return;
          a.src = candidates[idx++];
          a.load();
        };
        a.addEventListener("canplaythrough", () => {
          // Guard: only play if still ringing when the audio finishes loading
          if (callStateRef.current === CALL_STATE.RINGING && !setupOpenRef.current) {
            a.play().catch(() => {});
          }
        });
        a.addEventListener("error", () => tryNext());
        tryNext();
        ringtoneRef.current = a;
      }
      ringtoneRef.current.play().catch(() => {});
    }
  };

  // Ringtone lifecycle: stop immediately whenever we leave RINGING state
  useEffect(() => {
    if (state !== CALL_STATE.RINGING || setupOpen) {
      stopRingtone();
    }
    // Playing is triggered by startExperience (user gesture) — not here
  }, [state, setupOpen]);


  useEffect(() => {
    setQuestion(getQuestionText(questions[qIndex]));
    questionShownAtRef.current = Date.now();
    isFirstSpeechRef.current = false;
    delaySecondsRef.current = 0;
  }, [qIndex, questions]);

  // When the call is active, speak the current question (once) after transitions.
  useEffect(() => {
    if (state !== CALL_STATE.ACTIVE) return;
    if (paused || isRecording) return;
    if (!greetingSpokenRef.current) return;
    const questionItem = questions[qIndex];
    const q = getQuestionText(questionItem);
    if (!q) return;
    if (lastSpokenQuestionRef.current === q) return;
    lastSpokenQuestionRef.current = q;
    speakInterviewText(questionItem, "question");
  }, [state, paused, isRecording, qIndex, questions, speakInterviewText]);

  // Load dynamic questions once (optional; falls back to mock)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!sessionConfig) return;
        const role = configuredJobRole || "Software Engineer";
        const resQuestions = await generateQuestions(role, sessionConfigWithVoice, configuredDifficulty);
        if (mounted && Array.isArray(resQuestions) && resQuestions.length) {
          setQuestions(resQuestions);
          setQIndex(0);
        }
      } catch {
        // silent fallback
      }
    })();
    return () => {
      mounted = false;
    };
  }, [sessionConfigWithVoice, configuredJobRole, configuredDifficulty]);

  // Timer: only when active and not paused
  useEffect(() => {
    const running = state === CALL_STATE.ACTIVE || state === CALL_STATE.AI_THINKING;
    if (running && !paused && !timerRef.current) {
      timerRef.current = setInterval(() => setCallTime((t) => t + 1), 1000);
    }
    if ((!running || paused) && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [state, paused]);


  const resetAll = (nextState = CALL_STATE.RINGING) => {
    stopRecognition();
    stopRingtone();
    stopAllSpeech();

    greetingSpokenRef.current = false;
    lastSpokenQuestionRef.current = "";
    delaySecondsRef.current = 0;
    isFirstSpeechRef.current = false;

    setPaused(false);
    setCallTime(0);
    setQIndex(0);
    setMicOn(true);
    setIsRecording(false);
    setTranscript("");
    setLiveTranscript("");
    setAnswers([]);
    answersRef.current = [];
    setScore(0);
    setMetrics({ clarity: 0, pacing: 0, confidence: 0 });
    setDrawerOpen(false);
    setLastAnswerPayload(null);
    setLastRealtime(null);
    setCoachHint("");

    // Setup modal shown when starting a new call (best conversion UX + autoplay-safe)
    if (nextState === CALL_STATE.RINGING) {
      setupOpenRef.current = true;
      setSetupOpen(true);
    } else {
      setupOpenRef.current = false;
      setSetupOpen(false);
    }

    setState(nextState);
  };


  const onDecline = () => {
    resetAll(CALL_STATE.IDLE);
  };


  const onAnswer = () => {
    // Stop ringing immediately when the user answers.
    stopRingtone();
    setupOpenRef.current = false;
    setSetupOpen(false);

    // Greeting screen (live interview UI appears only after greeting ends)
    setState(CALL_STATE.CONNECTING);
    greetingSpokenRef.current = false;

    stopAllSpeech();

    const greeting = `Hello! Thank you for taking the time to speak with us today. I'm calling from InterviewGenieAI's recruitment team. This interview will consist of ${questions.length} questions about your background and experience. Please take your time with each answer.`;

    let finished = false;
    const finishGreeting = () => {
      if (finished) return;
      finished = true;
      greetingSpokenRef.current = true;
      lastSpokenQuestionRef.current = "";
      setState(CALL_STATE.ACTIVE);
    };

    // Prefer the configured ElevenLabs voice_id for greeting, then fallback to browser TTS.
    speakInterviewText(greeting, "greeting").then(finishGreeting).catch(finishGreeting);
    const words = greeting.trim().split(/\s+/).filter(Boolean).length;
    const fallbackMs = Math.max(12000, Math.min(42000, Math.round((words / 2.2) * 1000) + (configuredVoiceId ? 8000 : 0)));
    setTimeout(finishGreeting, fallbackMs);

    // Optional voice start command mode
    setWaitingForStart(false);
    setInterviewState("active");
  };


  const onTogglePause = () => {
    setPaused((p) => !p);
    // Pause should stop streaming + prevent analyze.
    if (!paused) {
      // about to pause — stop speech recognition if recording
      stopAllSpeech();
      lastSpokenQuestionRef.current = "";
      if (isRecording) {
        stopRecognition();
        setIsRecording(false);
      }
    }
  };

  const onRepeat = () => {
    if (paused) return;
    // Repeat keeps the same UI behavior; we just simulate AI thinking.
    setState(CALL_STATE.AI_THINKING);
    setTimeout(() => setState(CALL_STATE.ACTIVE), 550);
  };


  const runFinalAnalysis = async (answersList) => {
    setState(CALL_STATE.ANALYZING);
    try {
      const fillerWordsAgg = (answersList || []).reduce((acc, a) => {
        const fw = a?.analysis?.local_analysis?.filler_words || {};
        Object.entries(fw).forEach(([k, v]) => {
          acc[k] = (acc[k] || 0) + (Number(v) || 0);
        });
        return acc;
      }, {});

      const finalRes = await analyzeAnswersWithAI(
        (answersList || []).map((a) => ({
          question: a.question,
          transcript: a.transcript,
          duration: a.duration ?? 0,
          metrics: {
            clarity: a.metrics?.clarity ?? 0,
            pacing: a.metrics?.pacing ?? 0,
            // backendService.analyzeAnswersWithAI reads `responseTime` (camelCase)
            responseTime: a.metrics?.response_time ?? a.metrics?.responseTime ?? 0,
            professionalism: a.metrics?.professionalism ?? 0,
            tone: a.metrics?.tone ?? 0,
          },
        })),
        fillerWordsAgg,
        {
          clarity: avg(answersList || [], "clarity"),
          pacing: avg(answersList || [], "pacing"),
          confidence: avg(answersList || [], "confidence"),
        }
      );
      
      const interviewId = localStorage.getItem("current_interview_id") || null;
      // Submit independently — a missing interviewId or server error must not crash the score display.
      try {
        if (!interviewId) {
          console.warn("⚠️ No current_interview_id in localStorage — skipping result submission.");
        } else {
          await submitInterviewResultsPrepared({
            interviewId,
            finalRes,
            answersList,
            fillerWords: fillerWordsAgg,
          });
        }
      } catch (submitErr) {
        console.error("❌ Failed to submit interview results to backend:", submitErr);
        // Non-fatal: the user still sees their score and feedback.
      }

      const finalScore =
        typeof finalRes?.final_score === "number"
          ? finalRes.final_score
          : typeof finalRes?.local_analysis?.final_score === "number"
          ? finalRes.local_analysis.final_score
          : 0;
        
      setScore(clamp(Math.round(finalScore), 0, 100));
      setState(CALL_STATE.RESULTS);
      
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
      }, 6000);
      
    } catch (e) {
      // fallback: compute from local per-answer metrics
      const computed = Math.round(
        (avg(answersList || [], "clarity") + avg(answersList || [], "pacing") + avg(answersList || [], "confidence")) / 3
      );
      setScore(clamp(computed, 0, 100));
      setState(CALL_STATE.RESULTS);
    }
  };

  const runFarewellAndFinalize = async () => {
    setDrawerOpen(false);
    stopRecognition();
    stopRingtone();

    stopAllSpeech();

    setState(CALL_STATE.ENDING);

    const farewell =
      "Thank you for your time today. Your interview is now complete. We are generating your results. Goodbye.";

    const speakFarewell = () =>
      new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };

        speakInterviewText(farewell, "farewell").then(finish).catch(finish);

        // failsafe
        const words = farewell.trim().split(/\s+/).filter(Boolean).length;
        const fallbackMs = Math.max(12000, Math.min(42000, Math.round((words / 2.2) * 1000) + (configuredVoiceId ? 8000 : 0)));
        setTimeout(finish, fallbackMs);
      });

    await speakFarewell();

    await runFinalAnalysis(answersRef.current);
  };

  const onEnd = async () => {
    // If the user ends while recording, finalize the current answer first (no per-question summary).
    if (isRecording) {
      setIsRecording(false);
      await stopSpeakingAndScore({ openSummary: false });
    }
    await runFarewellAndFinalize();
  };

  function ensureRecognition() {

    if (recRef.current) return recRef.current;

    const rec = initializeSpeechRecognition(configuredLanguage, waitingForStart, isRecording, interviewState);
    if (!rec) return null;

    // Wrap submit handler so voice command can trigger scoring without UI changes
    const handleSubmitAnswer = async () => {
      if (!isRecording) return;
      setIsRecording(false);
      await stopSpeakingAndScore();
    };

    setupRecognitionCallbacks(
      rec,
      waitingForStartRef,
      setWaitingForStart,
      setInterviewState,
      (idx) => setQIndex(idx),
      setTranscript,
      setLiveTranscript,
      isRecordingRef,
      interviewStateRef,
      transcriptRef,
      handleSubmitAnswer,
      (t) => maybeRealtimeAnalyze(t),
      lastAnalysisTimeRef
    );

    rec.onerror = () => {
      // Keep UI stable; user can retry.
    };

    rec.onend = () => {
      // Some browsers auto-stop; if still recording, try to restart.
      if (isRecordingRef.current && !paused) {
        try {
          rec.start();
        } catch {
          // ignore
        }
      }
    };

    recRef.current = rec;
    return rec;
  }

  function stopRecognition() {
    try {
      abortRef.current?.abort?.();
    } catch {
      // ignore
    }
    abortRef.current = null;

    try {
      recRef.current?.stop?.();
    } catch {
      // ignore
    }
  }

  async function maybeRealtimeAnalyze(text) {
    const now = Date.now();
    const throttleMs = 1200;

    if (now - analyzeThrottleRef.current.lastAt < throttleMs) {
      analyzeThrottleRef.current.pending = true;
      return;
    }

    analyzeThrottleRef.current.lastAt = now;
    analyzeThrottleRef.current.pending = false;

    if (!text || text.trim().length < 6) return;
    if (!isRecording || paused) return;

    try {
      // Uses your JS backend service (auth headers + base URL are handled there)
      const resp = await analyzeTextWithBackend(
        text,
        getQuestionText(questions[qIndex]),
        answerStartTimeRef,
        delaySecondsRef.current || 0
      );

      // Map backend -> UI metrics
      const lm = resp?.local_analysis?.metrics || {};
      const clarity = clamp(Math.round(lm.clarity ?? 0), 0, 100);
      const pacing = clamp(Math.round(lm.pacing ?? 0), 0, 100);
      const confidence = pickConfidenceFromAnalysis(resp);

      const m = { clarity, pacing, confidence };
      setMetrics(m);
      setCoachHint(buildCoachHintFromMetrics(m));

      // Cache realtime insights for Strategy panel
      setLastRealtime({
        resp,
        coachHint: buildCoachHintFromMetrics(m),
      });
    } catch {
      // ignore (network/rate limit)
    } finally {
      // flush pending if needed
      if (analyzeThrottleRef.current.pending) {
        analyzeThrottleRef.current.pending = false;
        analyzeThrottleRef.current.lastAt = Date.now();
        // lightweight delayed retry
        setTimeout(() => {
          maybeRealtimeAnalyze(latestTextRef.current);
        }, 250);
      }
    }
  }

  async function startSpeaking() {
    setDrawerOpen(false);
    setState(CALL_STATE.ACTIVE);

    const rec = ensureRecognition();
    if (!rec) {
      // No speech recognition support; still allow text-based scoring after.
      setIsRecording(true);
      recordingStartedAtRef.current = Date.now();
      answerStartTimeRef.current = recordingStartedAtRef.current;
      return;
    }

    recordingStartedAtRef.current = Date.now();
    answerStartTimeRef.current = recordingStartedAtRef.current;
    latestTextRef.current = (transcript + liveTranscript).trim();

    try {
      rec.start();
    } catch {
      // Some browsers throw if already started; ignore.
    }

    setIsRecording(true);
  }

  async function stopSpeakingAndScore({ openSummary = true } = {}) {
    const finalText = `${transcript} ${liveTranscript}`.trim();
    stopRecognition();

    setState(CALL_STATE.AI_THINKING);

    const duration = Math.max(0.1, (Date.now() - recordingStartedAtRef.current) / 1000);
    const delay = delaySecondsRef.current || 0;

    let resp = null;
    try {
      resp = await analyzeTextWithBackend(
        finalText,
        getQuestionText(questions[qIndex]),
        answerStartTimeRef,
        delay
      );
    } catch {
      resp = null;
    }

    // Build the exact payload your UI expects
    const lm = resp?.local_analysis?.metrics || {};
    const clarity = clamp(Math.round(lm.clarity ?? metrics.clarity ?? 0), 0, 100);
    const pacing = clamp(Math.round(lm.pacing ?? metrics.pacing ?? 0), 0, 100);
    const confidence = resp ? pickConfidenceFromAnalysis(resp) : clamp(metrics.confidence ?? 0, 0, 100);

    const payload = {
      question: getQuestionText(questions[qIndex]),
      transcript: finalText,
      duration: Math.round(duration * 10) / 10,
      metrics: {
        // UI
        clarity,
        pacing,
        confidence,
        // extra (kept for your backend-style alignment)
        professionalism: lm.professionalism ?? 0,
        tone: lm.tone ?? 0,
        response_time: lm.response_time ?? 0,
      },
      analysis: resp || null,
    };

    setAnswers((prev) => {
      const next = [...prev, payload];
      answersRef.current = next;
      return next;
    });
    setLastAnswerPayload({
      question: payload.question,
      transcript: payload.transcript,
      metrics: {
        clarity: payload.metrics.clarity,
        pacing: payload.metrics.pacing,
        confidence: payload.metrics.confidence,
      },
    });

    // Update UI metrics + coach hint
    setMetrics({ clarity, pacing, confidence });
    setCoachHint(buildCoachHintFromMetrics({ clarity, pacing, confidence }));

    // Open summary drawer (unless we're ending the call)
    if (openSummary) {
      setDrawerOpen(true);
      setState(CALL_STATE.SUMMARY);
    } else {
      setDrawerOpen(false);
      // Keep UI calm; the caller may end right after.
      setState(CALL_STATE.ACTIVE);
    }
  }

  const onToggleMic = async () => {
    if (paused) return;

    // If mic muted → unmute
    if (!micOn) {
      setMicOn(true);
      return;
    }

    // Start/Stop recording
    if (!isRecording) {
      setIsRecording(true);
      await startSpeaking();
      return;
    }

    // Stop recording
    setIsRecording(false);
    await stopSpeakingAndScore();
  };

  const continueNext = async () => {
    setDrawerOpen(false);
    setTranscript("");
    setLiveTranscript("");
    setMetrics({ clarity: 0, pacing: 0, confidence: 0 });
    setLastAnswerPayload(null);
    setLastRealtime(null);
    setCoachHint("");

    const next = qIndex + 1;
    if (next < total) {
      setQIndex(next);
      setState(CALL_STATE.AI_THINKING);
      setTimeout(() => setState(CALL_STATE.ACTIVE), 650);
    } else {
      await runFarewellAndFinalize();
    }
  };

  const transcriptCombined = (transcript + liveTranscript).trim();
  const aiThinking = state === CALL_STATE.AI_THINKING;

  const panelOpen = state === CALL_STATE.ACTIVE || state === CALL_STATE.AI_THINKING || state === CALL_STATE.SUMMARY;

  // STAR signals (prefer AI star_method_analysis when available)
  const starSignals = useMemo(() => {
    const aiStar = lastRealtime?.resp?.ai_analysis?.star_method_analysis;
    return computeSTARSignals(transcriptCombined, aiStar);
  }, [transcriptCombined, lastRealtime]);

  const coachChecklist = useMemo(() => {
    const t = (transcriptCombined || "").trim();
    const words = t ? t.split(/\s+/).length : 0;
    const hasMetric = /\b(\d+%|\d+\s?(users|ms|s|secs|seconds|minutes|weeks|months|days|revenue|\$|k|m))\b/i.test(t);
    const hasTool = /\b(react|flask|sql|mysql|postgres|redis|celery|docker|kubernetes|aws|gcp|azure|api|rest|graphql)\b/i.test(t);

    return [
      {
        key: "one_line",
        ok: words >= 10,
        label: "Start with a one-line summary",
        meta: words >= 10 ? "Good opener length." : "Aim 10–15 words to set direction.",
      },
      {
        key: "one_example",
        ok: /\b(example|for instance|for example|project|case)\b/i.test(t) || words >= 35,
        label: "Give one concrete example",
        meta: "One story beats many fragments.",
      },
      {
        key: "tools",
        ok: hasTool,
        label: "Mention tools / actions",
        meta: "Shows ownership (what you actually did).",
      },
      {
        key: "metric",
        ok: hasMetric || starSignals.result,
        label: "Close with a measurable result",
        meta: "Add a number: %, time saved, users, performance, revenue.",
      },
    ];
  }, [transcriptCombined, starSignals.result]);

  return (
    <AppBackground>
      <SetupModal open={setupOpen} onStart={startExperience} questionsCount={questions.length} />
      <div className="min-h-screen w-full relative mt-16">
        <GlowBackdrop />
        <HairlineGrid />
        
        {showConfetti && <Confetti width={1500} height={800} />}
        
        {/* Top minimal header (desktop) */}
        <div className="hidden xl:flex items-center justify-between px-8 pt-6">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#D7D4FF]" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white tracking-tight">InterviewGenie AI</div>
              <div className="text-xs text-white/55">iPhone-first interview simulation • high-trust premium UX</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Chip icon={ShieldCheck}>Private</Chip>
            <Chip icon={Timer}>{formatTime(callTime)}</Chip>
            <IconButton onClick={() => resetAll(CALL_STATE.IDLE)} title="Exit">
              <X className="h-4 w-4" />
            </IconButton>
          </div>
        </div>

        {/* Center Stage: iPhone */}
        <div className="relative flex items-center justify-center overflow-hidden px-3 pt-5 pb-20 sm:px-4 sm:pt-10 sm:pb-24 xl:pb-10">
          <div className="relative">
            {/* subtle stage glow */}
            <div
              aria-hidden
              className="absolute -inset-10 rounded-[60px] blur-3xl
              bg-[radial-gradient(600px_420px_at_50%_20%,rgba(108,99,255,0.22),transparent_62%)]"
            />

            <IPhoneShell>
              {state === CALL_STATE.IDLE ? (
                <div className="h-full w-full flex items-center justify-center">
                  <div className="text-center px-10">
                    <div className="text-2xl font-semibold text-white">Call ended</div>
                    <div className="mt-2 text-sm text-white/60">Start again when ready.</div>
                    <div className="mt-6 flex justify-center">
                      <PrimaryButton onClick={() => resetAll(CALL_STATE.RINGING)} className="h-12 px-6">
                        Restart
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              ) : state === CALL_STATE.RINGING ? (
                <PhoneIncomingCall onDecline={onDecline} onAnswer={onAnswer} />
              ) : state === CALL_STATE.CONNECTING ? (
                <PhoneGreetingCall />
              ) : state === CALL_STATE.ENDING ? (
                <PhoneEndingCall />
              ) : (
                <PhoneLiveCall
                  qIndex={qIndex}
                  total={total}
                  micOn={micOn}
                  isRecording={isRecording}
                  paused={paused}
                  question={question}
                  transcript={transcriptCombined}
                  aiThinking={aiThinking}
                  onToggleMic={onToggleMic}
                  onTogglePause={onTogglePause}
                  onRepeat={onRepeat}
                  onEnd={onEnd}
                />
              )}
            </IPhoneShell>

            {/* Mobile quick controls (top overlay) */}
            <div className="xl:hidden absolute -top-3 left-1/2 -translate-x-1/2 w-[360px] sm:w-[390px]">
              <div className="flex items-center justify-between px-2">
                <Chip icon={Timer}>{formatTime(callTime)}</Chip>
                <button
                  type="button"
                  onClick={() => resetAll(CALL_STATE.IDLE)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-white/5 border border-white/10 text-white/75 hover:bg-white/10 transition"
                >
                  <X className="h-4 w-4" />
                  Exit
                </button>
              </div>
            </div>
          </div>

          {/* LEFT Strategy panel (desktop) */}
          <StrategyPanel
            open={panelOpen}
            question={question}
            lastRealtime={lastRealtime}
            transcriptPreview={transcriptCombined}
            starSignals={starSignals}
            coachChecklist={coachChecklist}
          />

          {/* AI Panel (desktop) */}
          <AIPanel
            open={panelOpen}
            state={state}
            time={callTime}
            qIndex={qIndex}
            total={total}
            metrics={metrics}
            micOn={micOn}
            paused={paused}
            isRecording={isRecording}
            coachHint={coachHint}
          />
        </div>

        {/* Analyzing overlay */}
        {state === CALL_STATE.ANALYZING ? (
          <div className="fixed inset-0 z-40 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/55 backdrop-blur-xl" />
            <Glass className="relative w-full max-w-[560px]">
              <GlowBackdrop />
              <HairlineGrid />
              <div className="p-10 text-center">
                <div className="text-3xl font-semibold text-white tracking-tight">Analyzing…</div>
                <div className="mt-3 text-sm text-white/65">Generating score, strengths, improvements, and next actions.</div>
                <div className="mt-8">
                  <Wave active variant="ai" />
                </div>
              </div>
            </Glass>
          </div>
        ) : null}

        {/* Summary Drawer */}
        <SummaryDrawer open={drawerOpen} onContinue={continueNext} payload={lastAnswerPayload} />

        {/* Results */}
        {state === CALL_STATE.RESULTS ? (
          <ResultsScreen score={score} answers={answers} totalTime={callTime} onRestart={() => resetAll(CALL_STATE.RINGING)} onExit={() => resetAll(CALL_STATE.IDLE)} />
        ) : null}

      </div>
    </AppBackground>
  );
}
